import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Layout, Empty, Button } from 'antd'
import fetch from 'isomorphic-fetch'
import dayjs from 'dayjs'
import { useAppSelector } from '../../app/hooks'
import * as SentryUtils from '../../utils/sentryUtils'
import desktopLib from '../../utils/desktopLib'
import sensors from '../../utils/sensors'
import parseQueryString from '../../utils/parseQueryString'
import { LeftOutlined } from '@ant-design/icons'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

// Excel 任务类型
interface ExcelTask {
  operateTime: string
  exportType: number
  membersNum: number
  startDate: string
  endDate: string
  finished: number
  fileName: string
  url: string
  contentNumber?: number
  requestId?: string
  groupID?: string
  fromPlace?: string
}

// 显示项类型
interface DisplayItem {
  type: 'attendanceGroup' | 'task'
  group?: ExcelTask[]
  task?: ExcelTask
  sortTime: number
}

const filterTasks = (
  tasks: ExcelTask[],
  fromPlace: string | undefined
): ExcelTask[] =>
  tasks
    .filter((task) => dayjs(task.operateTime).diff(dayjs(), 'days') > -7)
    .filter((task) => {
      // 只有当fromPlace是'rectifyRecords'时显示整改记录
      if (fromPlace === 'rectifyRecords') {
        return task.fromPlace === 'rectify'
      }
      // 其他页面进入的话，过滤掉整改记录
      if (fromPlace !== 'rectifyRecords') {
        return task.fromPlace !== 'rectify'
      }
      // 非整改页面进入时显示所有记录
      return true
    })
    .sort(
      (a, b) => dayjs(b.operateTime).valueOf() - dayjs(a.operateTime).valueOf()
    )

const { fromPlace } = parseQueryString()

function AttendanceExportList() {
  const countRef = useRef(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // local state
  const [tasks, setTasks] = useState<ExcelTask[]>([])

  // Redux state
  const user = useAppSelector((state) => state.main.user)

  // 事件埋点
  const handleEventTrack = useCallback(
    (actionType: string, exportType?: number) => {
      let string1: string | undefined = undefined
      switch (exportType) {
        case 0:
          string1 = 'attendanceDaily'
          break
        case 1:
          string1 = 'attendancePeriod'
          break
        case 2:
          string1 = 'photoDeskLedger'
          break
        case 3:
          string1 = 'allPhotoExportExcel'
          break
        default:
          break
      }
      sensors.track('web_get_action_data_download_record_page', {
        actionType,
        fromPlace,
        string1
      })
    },
    []
  )

  // 获取 Excel 状态
  const handleFetchExcelStatus = useCallback(() => {
    countRef.current = countRef.current + 1
    const excelTasksRaw = localStorage.getItem('excelTasks')
    if (!excelTasksRaw) {
      return
    }

    const allTasks: ExcelTask[] = JSON.parse(excelTasksRaw) || []
    const filteredTasks = filterTasks(allTasks, fromPlace as string | undefined)

    const pending = filteredTasks.filter((task) => task.finished > 0)
    const promises = pending.map((task) =>
      fetch(task.url, { method: 'head' })
        .then((res) => ({ url: task.url, ok: res.status === 200 }))
        .catch(() => ({ url: task.url, ok: false }))
    )

    if (promises.length === 0 || countRef.current >= 360) {
      if (timerRef.current) clearInterval(timerRef.current)
    }

    Promise.all(promises)
      .then((results) => {
        const finishedUrls = results.filter((r) => r.ok).map((r) => r.url)

        let updated = false
        for (let i = 0; i < allTasks.length; i++) {
          const t = allTasks[i]
          if (finishedUrls.includes(t.url) && t.finished > 0) {
            allTasks[i] = { ...t, finished: 0 }
            updated = true
          }
        }

        if (updated) {
          localStorage.setItem('excelTasks', JSON.stringify(allTasks))
        }

        const refreshedVisible = filterTasks(
          allTasks,
          fromPlace as string | undefined
        )
        setTasks(refreshedVisible)
      })
      .catch(SentryUtils.capturePromiseException)
  }, [])

  // componentDidMount
  useEffect(() => {
    const excelTasks = localStorage.getItem('excelTasks')
    if (excelTasks) {
      const allTasks: ExcelTask[] = JSON.parse(excelTasks)
      const filteredTasks = filterTasks(
        allTasks,
        fromPlace as string | undefined
      )
      setTasks(filteredTasks)

      timerRef.current = setInterval(handleFetchExcelStatus, 5000)
      handleEventTrack('enterPage')
    }

    // componentWillUnmount
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [handleFetchExcelStatus, handleEventTrack])

  // 保存文件
  const handleSave = useCallback(
    (task: ExcelTask) => {
      handleEventTrack('clickDownload', task.exportType)
      const a = document.createElement('a')
      a.href = task.url
      a.download = task.fileName
      a.click()
    },
    [handleEventTrack]
  )

  // 渲染任务标题
  const renderTaskTitle = useCallback((task: ExcelTask) => {
    let prefix = ''
    switch (task.exportType) {
      case 0:
        prefix = '每日统计'
        break
      case 1:
        prefix = '周月统计'
        break
      case 2:
        prefix = '照片明细'
        break
      default:
        break
    }

    if (prefix) {
      return `${prefix} | 申请时间：${task.operateTime}`
    }

    return `申请时间：${task.operateTime}`
  }, [])

  // 根据 storage 中的 requestId 分组：对每个 requestId 下包含考勤任务的组，渲染为一个合并的卡片
  const groupedByReq: Record<string, ExcelTask[]> = {}
  const noReqTasks: ExcelTask[] = []
  ;(tasks || []).forEach((t) => {
    if (t && t.requestId) {
      if (!groupedByReq[t.requestId]) groupedByReq[t.requestId] = []
      groupedByReq[t.requestId].push(t)
    } else {
      noReqTasks.push(t)
    }
  })

  const reqGroups = Object.keys(groupedByReq)
    .map((k) => groupedByReq[k])
    .filter((group) => group && group.length)

  const attendanceGroups = reqGroups.filter((group) =>
    group.some((t) => t.exportType === 0 || t.exportType === 1)
  )

  const nonAttendanceFromReq = reqGroups
    .filter((g) => !g.some((t) => t.exportType === 0 || t.exportType === 1))
    .flat()
  const remainingTasks = [...noReqTasks, ...nonAttendanceFromReq]

  // 合并为统一列表并按「最新申请时间」降序排序，使最新下载的排在最前
  const getGroupSortTime = (group: ExcelTask[]) =>
    Math.max(
      ...group
        .map((t) => (t.operateTime ? dayjs(t.operateTime).valueOf() : 0))
        .filter((v): v is number => v > 0),
      0
    )
  const displayItems: DisplayItem[] = [
    ...attendanceGroups.map((group) => ({
      type: 'attendanceGroup' as const,
      group,
      sortTime: getGroupSortTime(group)
    })),
    ...remainingTasks.map((task) => ({
      type: 'task' as const,
      task,
      sortTime: dayjs(task.operateTime).valueOf() || 0
    }))
  ].sort((a, b) => b.sortTime - a.sortTime)

  return (
    <Layout className="AttendanceExportList">
      <PageHeader
        ghost={false}
        style={{ borderBottom: '1px solid rgb(235, 237, 240)' }}
        title={
          <>
            {desktopLib.isDesktop && (
              <Button type="link" onClick={() => window.history.go(-1)}>
                <LeftOutlined />
                返回
              </Button>
            )}
            数据下载记录
          </>
        }
      />
      <Content className="content">
        <div className="list">
          {displayItems.length > 0 ? (
            displayItems.map((item, idx) => {
              if (item.type === 'attendanceGroup') {
                const group = item.group!
                const dailyTask = group.find((t) => t.exportType === 0)
                const periodTask = group.find((t) => t.exportType === 1)
                const photoTask =
                  group.find((t) => t.exportType === 2) ||
                  group.find((t) => t.exportType === 3)
                const membersNum = (group[0] && group[0].membersNum) || 0
                const operateTime =
                  group
                    .map((t) => t.operateTime)
                    .filter(Boolean)
                    .sort(
                      (a, b) => dayjs(a).valueOf() - dayjs(b).valueOf()
                    )[0] || ''
                const displayStart =
                  (dailyTask && dailyTask.startDate) ||
                  (periodTask && periodTask.startDate) ||
                  ''
                const displayEnd =
                  (dailyTask && dailyTask.endDate) ||
                  (periodTask && periodTask.endDate) ||
                  ''

                return (
                  <div className="task" key={`req-${idx}`}>
                    <div className="header">
                      <h3 style={{ display: 'block' }}>
                        <span>考勤统计</span>
                        <span style={{ marginLeft: 8 }}>
                          ｜ {displayStart}
                          {displayStart && displayEnd ? ' ~ ' : ''}
                          {displayEnd}
                        </span>
                      </h3>
                    </div>
                    <div className="detail">
                      <span>选择成员： {membersNum}人</span>
                      <span style={{ marginLeft: '20px' }}>
                        申请时间： {operateTime}
                      </span>
                    </div>
                    <div className="action">
                      <Button
                        type="primary"
                        loading={dailyTask && dailyTask.finished > 0}
                        disabled={!dailyTask}
                        onClick={() => dailyTask && handleSave(dailyTask)}
                      >
                        下载每日统计
                      </Button>
                      <Button
                        style={{ marginLeft: 8 }}
                        type="primary"
                        loading={periodTask && periodTask.finished > 0}
                        disabled={!periodTask}
                        onClick={() => periodTask && handleSave(periodTask)}
                      >
                        下载周月统计
                      </Button>
                      <Button
                        style={{ marginLeft: 8 }}
                        type="primary"
                        loading={photoTask && photoTask.finished > 0}
                        disabled={!photoTask}
                        onClick={() => photoTask && handleSave(photoTask)}
                      >
                        下载照片明细
                      </Button>
                    </div>
                  </div>
                )
              }

              const task = item.task!
              const groupName =
                (user?.groups || []).find(
                  (item) => item.group_id === task.groupID
                )?.group_name || ''
              let exportType = ''
              switch (task.exportType) {
                case 0:
                  exportType = '下载内容：每日统计'
                  break
                case 1:
                  exportType = '下载内容：周月统计'
                  break
                case 2:
                case 3:
                  exportType =
                    '下载条数：' +
                    (task.contentNumber === undefined
                      ? ''
                      : `${task.contentNumber}条`)
                  break
                case 4:
                  exportType = `整改记录下载条数：${
                    task.contentNumber === undefined ? '' : task.contentNumber
                  }条`
                  break
                default:
                  exportType = ''
                  break
              }
              return (
                <div className="task" key={`other-${idx}`}>
                  <div className="header">
                    <h3>
                      <span>{renderTaskTitle(task)}</span>
                      {groupName && <span>团队：{groupName}</span>}
                    </h3>
                  </div>
                  <div className="detail">
                    <span>{exportType}</span>
                    <span
                      style={{
                        display:
                          task.fromPlace === 'rectify' ? 'none' : 'inline-block'
                      }}
                    >
                      选择成员： {task.membersNum}人
                    </span>
                    <span>
                      时间范围: {task.startDate} ~ {task.endDate}
                    </span>
                  </div>
                  <div className="action">
                    {task.finished > 0 && countRef.current >= 360 ? (
                      <Button type="primary" disabled>
                        任务失败
                      </Button>
                    ) : (
                      <Button
                        type="primary"
                        loading={task.finished > 0}
                        onClick={() => handleSave(task)}
                      >
                        下载报表
                      </Button>
                    )}
                  </div>
                </div>
              )
            })
          ) : (
            <Empty style={{ marginTop: '200px' }} />
          )}
        </div>
      </Content>
    </Layout>
  )
}

export { AttendanceExportList }
export default AttendanceExportList
